import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Message } from 'primereact/message';
import { showTostify } from 'utils/interfaces';
import CustomToast from 'views/components/form-inputs/alert';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from 'utils/routes';
import { Editor } from 'primereact/editor';
import { Calendar } from 'primereact/calendar';
import { parseISO } from 'date-fns';
import moment from 'moment';
import { dealService } from 'services/deal.service';
import { storeService } from 'services/store.service';
import { InputField, RadioButton, SelectField } from 'views/components/form-inputs';

const initialValue = {
  title: "",
  description: "",
  type: "",
  payment_type: "view_now",
  discount_type: "percent",
  discount_code: "",
  amount: "",
  start_date: "",
  end_date: "",
  status: "active",
  store_id: ''
};

const AddEditDeals = () => {
  const params = useParams();
  const id = params.id;
  const [initialData, setInitialData] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);

  const toastRef = useRef(null);
  const navigate = useNavigate();

  const formik: any = useFormik({
    initialValues: initialData,
    validationSchema: Yup.object({
      title: Yup.string().required('Title is required'),
      type: Yup.string().required('Type is required'),
      payment_type: Yup.string().required('Payment type is required'),
      discount_code: Yup.string().required('Discount code is required'),
      discount_type: Yup.string().required('Discount Type is required'),
      amount: Yup.string().required('Amount is required'),
      start_date: Yup.date().required('Start date is required'),
      end_date: Yup.date().required('End date is required').min(Yup.ref('start_date'), 'End date must be after start date'),
      store_id: Yup.string().required('Store ID is required'),
      status: Yup.string().required('Status is required'),
    }),
    onSubmit: async (values: any) => {
      setLoading(true);
      try {
        let data;
        if (id) {
          delete values.id
          delete values.deal_number
          delete values.created_by
          delete values.created_at
          delete values.updated_at
          data = await dealService.updateDeals(id, values);
        } else {
          data = await dealService.createDeals(values);
        }
        if (!data.error) {
          showTostify(toastRef, 'success', data.message);
          formik.resetForm();
          setTimeout(() => {
            navigate(routes.deals);
          }, 800)
        } else {
          showTostify(toastRef, 'error', data.message);
        }
      } catch (err: any) {
        console.error('Error during submission:', err);
        showTostify(toastRef, 'error', err.message);
      } finally {
        setLoading(false);
      }
    }

  });

  useEffect(() => {
    const fetchDealDetails = async () => {
      if (id) {
        const data = await dealService.getDealDetails(id);
        if (!data.error) {
          setInitialData(data);
          formik.setValues(data);
        } else {
          showTostify(toastRef, 'error', data.message);
        }
      }
    };

    const fetchStores = async () => {
      const response = await storeService.getStoresReport({ 'filters[type]': 'local' });
      if (!response.error) {
        setStores(response.items || []);
      } else {
        showTostify(toastRef, 'error', response.message);
      }
    };

    fetchDealDetails();
    fetchStores();
  }, [id]);

  return (
    <div className="container" style={{ width: "800px", margin: "auto" }}>
      <div className='bg-white border-round-2xl'>
        <form onSubmit={formik.handleSubmit} >
          <CustomToast ref={toastRef} />
          {/* <div className='p-local-store-header' style={{ minHeight: "120px", backgroundColor: "#959CB6", width: "100%" }}>
          <h2 className='text-center text-white pt-3 m-0'>{id ? "Edit Deal" : "Create Deal"}</h2>
        </div> */}

          <div className="p-4 border-bottom-1 border-primary-color">
            <h2 className='text-start m-0'>{id ? "Edit Deal" : "Create Deal"}</h2>
          </div>

          <div className="grid m-0 py-4 p-3">
            <div className="col-12 md:col-6 lg:col-6">
              <InputField
                name="title"
                label="Deal Title"
                required={true}
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik?.touched.title && formik.errors.title}
              />
            </div>

            <div className="col-12 md:col-6 lg:col-6">
              <SelectField
                name="store_id"
                label="Store"
                filter
                showClear
                value={formik.values.store_id || ""}
                onChange={(e) => formik.setFieldValue('store_id', e.value)}
                options={stores.map((store: any) => ({ label: store.title, value: store.id }))}
                error={formik.touched.store_id && formik.errors.store_id}
                required={true}
                className="my-custom-class"
              />
            </div>

            <div className="col-12 md:col-6 lg:col-6">
              <InputField
                name="amount"
                type="number"
                label="Saving upto"
                required={true}
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik?.touched.amount && formik.errors.amount}
              />
            </div>

            <div className="col-6">
              <RadioButton
                className="custom-form-check"
                name="discount_type"
                value={formik.values.discount_type}
                onChange={(e: any) => formik.setFieldValue('discount_type', e.value)}
                options={[
                  { label: 'Fixed', value: 'fixed' },
                  { label: 'Percent', value: 'percent' },
                ]}
                required={true}
                label="Discount Type"
                error={formik.touched.discount_type && formik.errors.discount_type}
              />
            </div>

            <div className="col-6">
              <RadioButton
                className="custom-form-check"
                name="status"
                value={formik.values.status}
                onChange={(e: any) => formik.setFieldValue('status', e.value)}
                options={[
                  { label: 'Active', value: 'active' },
                  { label: 'Draft', value: 'draft' },
                ]}
                required={true}
                label="Status"
                error={formik.touched.status && formik.errors.status}
              />
            </div>

            <div className="col-12 md:col-6 lg:col-6">
              <RadioButton
                className="custom-form-check"
                name="type"
                value={formik.values.type}
                onChange={(e: any) => formik.setFieldValue('type', e.value)}
                options={[
                  { label: 'Standard', value: 'standard' },
                  { label: 'Gift voucher', value: 'gift_voucher' },
                  { label: 'Exclusive', value: 'exclusive' },
                ]}
                required={true}
                label="Deal Type"
                error={formik.touched.type && formik.errors.type}
              />
            </div>

            <div className="col-12 md:col-6 lg:col-6">
              <RadioButton
                className="custom-form-check"
                name="payment_type"
                value={formik.values.payment_type}
                onChange={(e: any) => formik.setFieldValue('payment_type', e.value)}
                options={[
                  { label: 'Call now', value: 'call_now' },
                  { label: 'Pay now', value: 'pay_now' },
                  { label: 'View now', value: 'view_now' },
                ]}
                required={true}
                label="Payment Type"
                error={formik.touched.payment_type && formik.errors.payment_type}
              />
            </div>

            {formik.values?.payment_type === "call_now" &&
              <div className="col-12 md:col-6 lg:col-6">
                <InputField
                  name="deal.phone_number1"
                  label="Phone Number"
                  required={true}
                  value={formik.values?.phone_number1}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik?.touched?.phone_number1 && formik.errors?.phone_number1}
                />
              </div>}
            {formik.values?.payment_type === "pay_now" &&
              <div className="col-12 md:col-6 lg:col-6">
                <InputField
                  name="deal.pay_amount"
                  label="Pay Amount"
                  required={true}
                  value={formik.values?.pay_amount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik?.touched?.pay_amount && formik.errors?.pay_amount}
                />
              </div>}
            {formik.values?.payment_type === "view_now" &&
              <div className="col-12 md:col-6 lg:col-6">
                <InputField
                  name="deal.discount_code"
                  label="Discount code"
                  required={true}
                  value={formik.values?.discount_code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik?.touched?.discount_code && formik.errors?.discount_code}
                />
              </div>}


            <div className="col-12 md:col-6 lg:col-6">
              <div className="form-group mb-2">
                <label htmlFor="start_date">Start Date<span className='required-field'>*</span></label>
                <Calendar
                  id="start_date"
                  name="start_date"
                  value={formik.values.start_date ? parseISO(formik.values.start_date) : null}
                  onChange={(e) => {
                    if (e.value) {
                      const formattedDate = moment(e.value).toISOString();
                      formik.setFieldValue('start_date', formattedDate);
                    } else {
                      formik.setFieldValue('start_date', null);
                    }
                  }}
                  showIcon
                  minDate={new Date()}
                  placeholder="Enter here"
                  className={formik.touched?.start_date && formik.errors?.start_date ? 'p-invalid' : ''}
                />
                {formik.touched?.start_date && formik.errors?.start_date && (
                  <Message severity="error" text={formik.errors.start_date} />
                )}
              </div>
            </div>

            <div className="col-12 md:col-6 lg:col-6">
              <div className="form-group mb-2">
                <label htmlFor="end_date">End Date<span className='required-field'>*</span></label>
                <Calendar
                  id="end_date"
                  name="end_date"
                  value={formik.values?.end_date ? parseISO(formik.values.end_date) : null}
                  onChange={(e) => {
                    if (e.value) {
                      const formattedDate = moment(e.value).toISOString();
                      formik.setFieldValue('end_date', formattedDate);
                    } else {
                      formik.setFieldValue('end_date', null);
                    }
                  }}
                  minDate={new Date()}
                  showIcon
                  placeholder="Enter here"
                  className={formik.touched?.end_date && formik.errors?.end_date ? 'p-invalid' : ''}
                />
                {formik.touched?.end_date && formik.errors?.end_date && (
                  <Message severity="error" text={formik.errors.end_date} />
                )}
              </div>
            </div>

            <div className="col-12">
              <div className="form-group mb-2">
                <label htmlFor=".title">Description
                  {/* <span className='required-field'>*</span> */}
                </label>
                <Editor
                  value={formik.values?.description}
                  onTextChange={(e) => formik.setFieldValue('description', e.htmlValue || '')}
                  style={{ height: '100px' }}
                  placeholder="Enter here"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched?.description && formik.errors?.description && (
                  <Message severity="error" text={formik.errors?.description} />
                )}
              </div>
            </div>

            <div className="col-12 text-center">
              <Button
                label={id ? "Edit Deal" : "Add Deal"}
                type="submit"
                className="primary"
                loading={loading}
              // disabled={!formik.isValid || formik.isSubmitting || loading}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditDeals;
